'use client';

import React, { useEffect, useState, Suspense } from 'react';
import { motion } from 'framer-motion';

function TermsOfServiceContent() {
  const [particles, setParticles] = useState<Array<{ x: number; y: number }>>([]);

  useEffect(() => {
    // Initialize particles only on client side
    setParticles(
      [...Array(50)].map(() => ({
        x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1000),
        y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 1000)
      }))
    );
  }, []);

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden -mt-24">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
        <div className="absolute inset-0 bg-[url('/assets/grid-pattern.svg')] bg-[length:40px_40px] opacity-[0.04]"></div>
        <div className="absolute inset-0 bg-[url('/assets/noise.svg')] opacity-[0.02] mix-blend-overlay"></div>
        
        {/* Animated glowing orbs */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -left-40 w-96 h-96 bg-gradient-to-br from-primary-500/30 to-blue-500/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-gradient-to-br from-purple-500/30 to-pink-500/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-blue-500/20 via-purple-500/20 to-pink-500/20 rounded-full blur-3xl animate-pulse delay-2000"></div>
        </div>

        {/* Binary data particles */}
        <div className="absolute inset-0 overflow-hidden">
          {particles.map((particle, i) => (
            <motion.div
              key={i}
              className="absolute text-white/10 font-mono text-sm"
              initial={{
                x: particle.x,
                y: particle.y,
                opacity: 0
              }}
              animate={{
                y: [null, -100],
                opacity: [0, 1, 0],
                x: [null, particle.x + (Math.random() * 100 - 50)]
              }}
              transition={{
                duration: Math.random() * 10 + 10,
                repeat: Infinity,
                delay: Math.random() * 5,
                ease: "linear"
              }}
            >
              {Math.random() > 0.5 ? '1' : '0'}
            </motion.div>
          ))}
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center">
            <motion.h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="text-transparent bg-clip-text bg-gradient-rainbow">Terms of Service</span>
            </motion.h1>
            <motion.p 
              className="text-xl text-slate-300 max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Our commitment to providing reliable and secure hosting services
            </motion.p>
          </div>
        </div>
      </section>

      {/* Terms of Service Content */}
      <section className="py-20 relative overflow-hidden bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="prose prose-invert max-w-none"
          >
            <div className="bg-slate-800/50 rounded-xl p-8 mb-8">
              <p className="text-slate-300 mb-4">Effective Date: 1 Nov 2024</p>
              <p className="text-slate-300">Welcome to 1 Dollar Cloud, a service provided by Global Ads Media, LLC. These Terms of Service ("Terms") govern your use of our hosting services, and by accessing or using our website and services, you agree to comply with and be bound by these Terms. If you do not agree with any part of these Terms, you must immediately discontinue using our services.</p>
            </div>

            {[
              {
                title: "1. Account Registration and User Obligations",
                content: [
                  "To use 1 Dollar Cloud hosting services, you must first register for an account. You can sign up using your email address or through third-party services such as Google, Facebook, or Twitter. Upon registration, you agree to provide accurate, current, and complete information, including your full name, valid email address, phone number, and other details required to create your account.",
                  "Additionally, we may require identity verification through a government-issued ID card or other documents for security and fraud prevention purposes. You are responsible for maintaining the confidentiality of your login credentials, and you agree to notify us immediately of any unauthorized use of your account.",
                  "By signing up for our hosting services, you agree to provide the necessary details to complete your registration process. You also agree to keep your information up to date by updating your profile whenever necessary."
                ]
              },
              {
                title: "2. Payment Terms and Billing",
                content: [
                  "In order to use 1 Dollar Cloud services, you must provide valid payment information. We accept credit and debit card payments through secure third-party payment processors. By entering your payment details, you authorize 1 Dollar Cloud to charge the associated payment method for the services you select.",
                  "Billing Cycle: Upon purchase, you will be billed according to the selected hosting package and billing cycle (e.g., monthly, yearly). Payments are due in advance for each billing cycle, and you agree to pay for the services as specified.",
                  "If payment is not received by the due date, we may suspend or terminate your access to the hosting services, and your account and data may be deleted.",
                  "Recurring Charges: By signing up for our services, you authorize us to automatically renew your hosting services at the end of each billing period, charging the same payment method on file. You can manage your payment method and cancel renewal at any time via your account settings."
                ]
              },
              {
                title: "3. User Data and Privacy",
                content: [
                  "When you use 1 Dollar Cloud hosting services, you may store personal or business-related data, including but not limited to emails, documents, media, and other content on our servers. We take privacy and data security seriously and handle your data in accordance with our Privacy Policy. You are solely responsible for the content you upload and store and for ensuring that it complies with applicable laws, including data protection regulations.",
                  "Data Security: We implement industry-standard security measures to protect your data, including encryption, firewalls, and access control mechanisms. However, you acknowledge that no system is completely secure, and we cannot guarantee that unauthorized access to your data will never occur.",
                  "By using our services, you grant us permission to store, process, and manage your data in line with our Privacy Policy. We will not share or sell your data to third parties without your consent, except in cases where it is required by law or to fulfill the services you requested."
                ]
              },
              {
                title: "4. Usage of Services",
                content: [
                  "You agree to use 1 Dollar Cloud services in compliance with all applicable laws and regulations. Prohibited activities include but are not limited to:",
                  "• Using our services for illegal, harmful, or fraudulent activities.",
                  "• Uploading or transmitting viruses, malware, or other harmful content that could damage or disrupt the integrity of our services or other users' websites.",
                  "• Violating the intellectual property rights of others, including uploading pirated content or distributing copyrighted materials without permission.",
                  "• Engaging in spam or unsolicited communications, including email spam or other types of bulk messaging.",
                  "We reserve the right to suspend or terminate your account if you violate these terms or engage in activities that could harm the integrity or reputation of 1 Dollar Cloud and its users."
                ]
              }
            ].map((section, index) => (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-slate-800/50 rounded-xl p-8 mb-8"
              >
                <h2 className="text-2xl font-bold text-white mb-6">{section.title}</h2>
                {section.content.map((paragraph, i) => (
                  <p key={i} className="text-slate-300 mb-4">{paragraph}</p>
                ))}
              </motion.div>
            ))}

            {[
              {
                title: "5. Refund Policy",
                content: "We understand that you may want to try out our services before making a long-term commitment. Therefore, we offer a 7-day refund policy. If you are not satisfied with the services you receive, you may request a full refund within 7 days of your initial purchase. After the 7-day period, we do not offer refunds for any reason. This policy applies only to the initial purchase of hosting services and does not apply to domain registrations or other third-party services. To request a refund, please contact our support team within 7 days of your purchase. Refunds will be processed through the same payment method you used for the transaction. You can find more information on our refund policy here- https://1dollarcloud.com/refund-policy"
              },
              {
                title: "6. Service Availability and Uptime",
                content: "We strive to provide 99.99% uptime for our hosting services. However, due to factors beyond our control, including network outages, server failures, or maintenance, we do not guarantee continuous availability. We reserve the right to perform maintenance on our servers at any time, but we will make efforts to minimize any disruptions. Service Interruptions: While we will make reasonable efforts to ensure service continuity, we are not liable for any interruptions caused by factors such as internet outages, hardware malfunctions, or force majeure events."
              },
              {
                title: "7. Termination of Services",
                content: "We may suspend or terminate your account and access to 1 Dollar Cloud services if you violate these Terms, engage in fraudulent activities, or fail to comply with our policies. Upon termination, your account and data may be deleted. If you wish to cancel your account, you may do so through the account settings on your dashboard. Upon cancellation, your data may be retained for a period of time as required by law or for accounting purposes."
              },
              {
                title: "8. Limitation of Liability",
                content: "Global Ads Media, LLC., and 1 Dollar Cloud shall not be held liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of our services, including but not limited to loss of data, revenue, or business profits. Our total liability for any claims related to these Terms shall be limited to the amount you paid for the services in the 30 days prior to the event giving rise to the claim."
              },
              {
                title: "9. Modifications to Terms and Services",
                content: "We reserve the right to update or modify these Terms at any time. We will notify users of any material changes to the Terms, which will become effective immediately upon posting. It is your responsibility to review these Terms periodically to stay informed of any changes. Continued use of our services after any changes to the Terms will constitute your acceptance of those changes."
              },
              {
                title: "10. Governing Law and Dispute Resolution",
                content: "These Terms are governed by and construed in accordance with the laws of USA, without regard to its conflict of law provisions. Any disputes or claims arising out of or related to these Terms will be resolved through binding arbitration in USA. Both parties agree to submit to the exclusive jurisdiction of the courts in USA."
              }
            ].map((section, index) => (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-slate-800/50 rounded-xl p-8 mb-8"
              >
                <h2 className="text-2xl font-bold text-white mb-6">{section.title}</h2>
                <p className="text-slate-300">{section.content}</p>
              </motion.div>
            ))}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-slate-800/50 rounded-xl p-8"
            >
              <h2 className="text-2xl font-bold text-white mb-6">11. Contact Information</h2>
              <p className="text-slate-300 mb-4">If you have any questions or concerns about these Terms, please contact us at:</p>
              <div className="text-slate-300">
                <p className="font-semibold">1 Dollar Cloud</p>
                <p>a service of Global Ads Media, LLC.</p>
                <p>66 West Flagler Street Miami Florida 33130 USA</p>
                <p className="mt-4">Email:</p>
                <p>info@1dollarcloud.com</p>
                <p>info@globaladsmedia.us</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-slate-300 text-center mt-8"
            >
              By using our services, you acknowledge that you have read, understood, and agree to these Terms of Service.
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default function TermsOfServicePage() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-slate-900 flex items-center justify-center">
      <div className="text-white">Loading...</div>
    </div>}>
      <TermsOfServiceContent />
    </Suspense>
  );
}